import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ClientPaymentForm from './ClientPaymentForm';
import './Catering.css';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import TrashIcon from '@mui/icons-material/DeleteOutline';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

// Sub images (premade)
import londonImg from './menu/broil.png';
import turkeyImg from './menu/turkey.png';
import veggieImg from './menu/veggie.png';
import italianImg from './menu/italian.png';
import cubanImg from './menu/cuban.png';
import hamImg from './menu/ham.png';

// Chips/Cookies
import bbqImg from './menu/bbq.png';
import seasaltImg from './menu/seasalt.png';
import jalapenoImg from './menu/jalapeno.png';
import onionImg from './menu/onion.png';
import cookieImg from './ccc.png';

// Drinks
import waterImg from './water.png';
import cokeImg from './coke.png';
import dietImg from './diet.png';
import spriteImg from './sprite.png';
import dewImg from './dew.png';
import fantaImg from './fanta.png';

// Pill image
import homeImg from './home.jpg';
import appIcon from './menu/appIcon.png';
import customImg from './menu/custom.png';

// Custom (MYO) asset imports from /menu/
import imgHam from './toppings/ham.png';
import imgTurkey from './toppings/turkey.png';
import imgSalami from './toppings/salomi.png';          // matches your mobile filename
import imgSopressata from './toppings/Sopressata.png';  // capital S per your mobile list
import imgBroil from './toppings/broil.png';

import imgSwiss from './toppings/swiss.png';
import imgProvolone from './toppings/provalone.png';    // matches your mobile filename
import imgCheddar from './toppings/cheddar.png';

import imgBacon from './toppings/bacon.png';
import imgTomato from './toppings/tomato.png';
import imgCucumber from './toppings/cucumber.png';
import imgRomaine from './toppings/romaine.png';
import imgSpinach from './toppings/spinach.png';
import imgOnion from './toppings/onion.png';
import imgPickle from './toppings/pickle.png';
import imgOlive from './toppings/olive.png';
import imgGreenPepper from './toppings/green.png';
import imgJalapeno from './toppings/jalapeno.png';
import imgBananaPepper from './toppings/baana.png';  // matches your mobile filename
import imgSauce from './toppings/sauce.png';

// MUI
import {
  IconButton,
  Dialog,
  DialogContent,
  Checkbox,
  Button,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarIcon from '@mui/icons-material/Star';

// ===== Ingredient Pool Constants =====
const CUSTOM_SUB_MEAT_NAMES = new Set([
  'virginia ham',
  'ovengold turkey',
  'genoa salami',
  'hot sopressata',
  'london broil roast beef',
  'roasted pork',
]);

const EXTRA_MEAT_NAMES = new Set([
  'virginia ham',
  'ovengold turkey',
  'genoa salami',
  'hot sopressata',
  'london broil roast beef',
  'roasted pork',
]);

const EXTRA_CHEESE_NAMES = new Set([
  'baby swiss',
  'aged wisconsin sharp cheddar',
  'smoked provolone',
]);

const EXTRA_SAUCE_NAMES = new Set([
  'mustang sauce',
  'chipotle sauce',
  'parmy sauce',
  'cuban mustard',
  'oil and vinegar',
  'oil',
  'red wine vinegar',
  'mayo',
  'spicy mustard',
  'yellow mustard',
  'honey mustard',
]);

const EXTRA_TOPPING_NAMES = new Set([
  'bacon',
  'roma tomatoes',
  'cucumbers',
  'romaine lettuce',
  'baby spinach',
  'diced red onion',
  'dill pickles',
  'black olives',
  'green bell peppers',
  'jalapenos',
  'banana peppers',
]);

const ingredientMap = {
  'Italian': {
    desc: 'Virginia Ham, Genoa Salami, Hot Sopressata, Smoked Provolone, Fresh Romaine Lettuce, and Parmy Sauce',
    list: ['Virginia Ham', 'Genoa Salami', 'Hot Sopressata', 'Smoked Provolone', 'Romaine Lettuce', 'Parmy Sauce']
  },
  'Cuban': {
    desc: 'Virginia Ham, Genoa Salami, Roasted Pork, Baby Swiss, Dill Pickles and Cuban Mustard',
    list: ['Virginia Ham', 'Genoa Salami', 'Roasted Pork', 'Baby Swiss', 'Dill Pickles', 'Cuban Mustard']
  },
  'Turkey': {
    desc: 'Ovengold Turkey with Baby Swiss, Fresh Romaine Lettuce, Roma Tomatoes, Dill Pickles and Mayo',
    list: ['Ovengold Turkey', 'Baby Swiss', 'Romaine Lettuce', 'Roma Tomatoes', 'Dill Pickles', 'Mayo']
  },
  'Virginia Ham': {
    desc: 'Virginia Ham with Aged Wisconsin Sharp Cheddar, Fresh Romaine Lettuce, Roma Tomatoes, Dill Pickles and Mayo',
    list: ['Virginia Ham', 'Aged Wisconsin Sharp Cheddar', 'Romaine Lettuce', 'Roma Tomatoes', 'Dill Pickles', 'Mayo']
  },
  'Veggie': {
    desc: 'Fresh Romaine, Baby Spinach, Diced Red Onion, Roma Tomatoes, Dill Pickles, Banana Peppers, Jalapenos, Green Bell Peppers, Black Olives, Cucumbers with Oil and Red Wine Vinegar',
    list: ['Romaine Lettuce', 'Baby Spinach', 'Diced Red Onion', 'Roma Tomatoes', 'Dill Pickles', 'Banana Peppers', 'Jalapenos', 'Green Bell Peppers', 'Black Olives', 'Cucumbers', 'Oil', 'Red Wine Vinegar']
  },
  'London Broil': {
    desc: 'London Broil Roast Beef w/ Aged Wisconsin Sharp Cheddar, Romaine Lettuce, Roma Tomatoes, Dill Pickles & Mustang Sauce',
    list: ['London Broil Roast Beef', 'Aged Wisconsin Sharp Cheddar', 'Romaine Lettuce', 'Roma Tomatoes', 'Dill Pickles', 'Mustang Sauce']
  }
};


const EXTRA_OPTIONS = [
  { key: 'doubleMeat', label: 'Double Meat', price: 2.75 },
  { key: 'doubleCheese', label: 'Double Cheese', price: 2.0 },
];

const TOAST_OPTIONS = [
  { key: 'toasted', label: 'Toasted' },
  { key: 'not_toasted', label: 'Not Toasted' },
];

// List of sauces for extras
const SAUCE_OPTIONS = [
  "Mick's Mustang Sauce",
  'Chipotle Sauce',
  'Parmy Sauce',
  'Cuban Mustard',
  'Oil and Vinegar',
  'Mayo',
  'Spicy Mustard',
  'Yellow Mustard',
  'Honey Mustard'
];

// ===== Make Your Own (MYO) configuration =====
// Pricing constants
const MYO_BASE_6 = 8.99; // Base price for 6" (2 meats, up to 3 cheeses included)
const MYO_BASE_12 = 15.99; // Base price for 12" (2 meats, up to 3 cheeses included)
const MYO_EXTRA_MEAT = 2.75; // 3rd protein
const MYO_EXTRA_LB_MEAT = 3.75; // 3rd protein if London Broil
const MYO_LONDON_BROIL_UPCHARGE = 1.00; // Extra $1 if London Broil selected
const MYO_DOUBLE_MEAT = 2.75; // Double meat standard
const MYO_DOUBLE_MEAT_LB = 3.75; // Double meat with London Broil
const MYO_DOUBLE_CHEESE = 2.00; // Double cheese
const MYO_BACON_6 = 1.25; // Bacon for 6"
const MYO_BACON_12 = 2.50; // Bacon for 12"
const MYO_EXTRA_SAUCE = 0.75; // Extra sauce

const MYO_SIZES = [
  { key: '6', label: '6 inch', basePrice: MYO_BASE_6 },
  { key: '12', label: '12 inch', basePrice: MYO_BASE_12 },
];

const pointsFromPrice = (price) => Math.round(Number(price || 0) * 2.55);

const MYO_MEATS = [
  { label: 'Virginia Ham', img: imgHam },
  { label: 'Ovengold Turkey', img: imgTurkey },
  { label: 'Genoa Salami', img: imgSalami },
  { label: 'Hot Sopressata', img: imgSopressata },
  { label: 'London Broil Roast Beef', img: imgBroil },
  { label: 'Roasted Pork', img: imgHam },
];

const MYO_CHEESES = [
  { label: 'Baby Swiss', img: imgSwiss },
  { label: 'Aged Wisconsin Sharp Cheddar', img: imgCheddar },
  { label: 'Smoked Provolone', img: imgProvolone },
];

const MYO_TOPPINGS = [
  { label: 'Bacon', img: imgBacon },
  { label: 'Roma Tomatoes', img: imgTomato },
  { label: 'Cucumbers', img: imgCucumber },
  { label: 'Romaine Lettuce', img: imgRomaine },
  { label: 'Baby Spinach', img: imgSpinach },
  { label: 'Diced Red Onion', img: imgOnion },
  { label: 'Dill Pickles', img: imgPickle },
  { label: 'Black Olives', img: imgOlive },
  { label: 'Green Bell Peppers', img: imgGreenPepper },
  { label: 'Jalapenos', img: imgJalapeno },
  { label: 'Banana Peppers', img: imgBananaPepper },
];

const MYO_SAUCES = [
  { key: 'micks', label: 'Mustang Sauce', img: imgSauce },
  { key: 'chipotle', label: 'Chipotle Sauce', img: imgSauce },
  { key: 'parmy', label: 'Parmy Sauce', img: imgSauce },
  { key: 'cuban', label: 'Cuban Mustard', img: imgSauce },
  { key: 'oilvin', label: 'Oil and Vinegar', img: imgSauce },
  { key: 'oil', label: 'Oil', img: imgSauce },
  { key: 'redwinevinegar', label: 'Red Wine Vinegar', img: imgSauce },
  { key: 'mayo', label: 'Mayo', img: imgSauce },
  { key: 'spicy', label: 'Spicy Mustard', img: imgSauce },
  { key: 'yellow', label: 'Yellow Mustard', img: imgSauce },
  { key: 'honey', label: 'Honey Mustard', img: imgSauce },
  { key: 'parmy_legacy', label: 'Parmy Sauce', img: imgSauce },
  { key: 'cuban_legacy', label: 'Cuban Mustard', img: imgSauce },
  { key: 'mustang_legacy', label: 'Mustang Sauce', img: imgSauce },
];

const MYO_TOAST = [
  { key: 'toasted', label: 'Toasted' },
  { key: 'not_toasted', label: 'Not Toasted' },
];

// ========================================================================

const ClientOrders = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const orderingDisabledMessage = 'Not Active! Please order through Toast for now.';
      // Success dialog state
      const [showSuccess, setShowSuccess] = useState(false);
      const [orderNumber, setOrderNumber] = useState("");
      const [readyTime, setReadyTime] = useState("");
    // Payment form fields
    const [paymentName, setPaymentName] = useState("");
    const [paymentEmail, setPaymentEmail] = useState("");
  const [confirmAdd, setConfirmAdd] = useState(null); // holds {name,img}

  const [isSignedIn, setIsSignedIn] = useState(false);
  const [points, setPoints] = useState(0);

  const [subs] = useState([
    {
      name: 'London Broil',
      img: londonImg,
      price6: 8.50,
      price12: 16.50,
      points6: 22,
      points12: 42,
      defaultMeats: ['london broil roast beef'],
      defaultCheeses: ['aged wisconsin sharp cheddar'],
      defaultToppings: ['romaine lettuce', 'roma tomatoes', 'dill pickles'],
      defaultSauces: ['mustang sauce']
    },
    {
      name: 'Turkey',
      img: turkeyImg,
      price6: 8.00,
      price12: 13.25,
      points6: 20,
      points12: 34,
      defaultMeats: ['ovengold turkey'],
      defaultCheeses: ['baby swiss'],
      defaultToppings: ['romaine lettuce', 'roma tomatoes', 'dill pickles'],
      defaultSauces: ['mayo']
    },
    {
      name: 'Veggie',
      img: veggieImg,
      price6: 7.50,
      price12: 12.95,
      points6: 19,
      points12: 33,
      defaultMeats: [],
      defaultCheeses: [],
      defaultToppings: ['romaine lettuce', 'baby spinach', 'diced red onion', 'roma tomatoes', 'dill pickles', 'banana peppers', 'jalapenos', 'green bell peppers', 'black olives', 'cucumbers'],
      defaultSauces: ['oil', 'red wine vinegar']
    },
    {
      name: 'Italian',
      img: italianImg,
      price6: 8.25,
      price12: 12.95,
      points6: 21,
      points12: 33,
      defaultMeats: ['virginia ham', 'genoa salami', 'hot sopressata'],
      defaultCheeses: ['smoked provolone'],
      defaultToppings: ['romaine lettuce'],
      defaultSauces: ['parmy sauce']
    },
    {
      name: 'Cuban',
      img: cubanImg,
      price6: 8.25,
      price12: 12.95,
      points6: 21,
      points12: 33,
      defaultMeats: ['virginia ham', 'genoa salami', 'roasted pork'],
      defaultCheeses: ['baby swiss'],
      defaultToppings: ['dill pickles'],
      defaultSauces: ['cuban mustard']
    },
    {
      name: 'Virginia Ham',
      img: hamImg,
      price6: 8.00,
      price12: 13.25,
      points6: 20,
      points12: 34,
      defaultMeats: ['virginia ham'],
      defaultCheeses: ['aged wisconsin sharp cheddar'],
      defaultToppings: ['romaine lettuce', 'roma tomatoes', 'dill pickles'],
      defaultSauces: ['mayo']
    }
  ]);

  const [chips] = useState([
    { name: 'BBQ Chips', img: bbqImg, price: 1.75, points: 5 },
    { name: 'Sea Salt Chips', img: seasaltImg, price: 1.75, points: 5 },
    { name: 'Jalapeno Chips', img: jalapenoImg, price: 1.75, points: 5 },
    { name: 'Sweet Maui Onion Chips', img: onionImg, price: 1.75, points: 5 },
    { name: 'Cookie', img: cookieImg, price: 2.00, points: 5 },
  ]);

  const [drinks] = useState([
    { name: 'Water', img: waterImg, price: 1.75, points: 5 },
    { name: 'Coke', img: cokeImg, price: 1.75, points: 5 },
    { name: 'Diet Coke', img: dietImg, price: 1.75, points: 5 },
    { name: 'Sprite', img: spriteImg, price: 1.75, points: 5 },
    { name: 'Fanta', img: fantaImg, price: 1.75, points: 5 },
    { name: 'Mountain Dew', img: dewImg, price: 1.75, points: 5 },
  ]);

  // Sub Customization Dialog State (premade subs)
  const [openSub, setOpenSub] = useState(null); // {name,img,price}
  const [subMeats, setSubMeats] = useState(new Set()); // Selected meats for premade sub
  const [subCheeses, setSubCheeses] = useState(new Set()); // Selected cheeses for premade sub
  const [subToppings, setSubToppings] = useState(new Set()); // Selected toppings for premade sub
  const [subSauces, setSubSauces] = useState(new Set()); // Selected sauces for premade sub
  const [lengthChoice, setLengthChoice] = useState('12'); // '6' | '12'
  const [selectedExtras, setSelectedExtras] = useState({}); // {key: boolean}
  const [toastChoice, setToastChoice] = useState('toasted'); // 'toasted' | 'not_toasted'

  // Make Your Own dialog state + selections
  const [showMakeYourOwn, setShowMakeYourOwn] = useState(false);
  const [myoSize, setMyoSize] = useState(''); // '6' | '12'
  const [myoMeats, setMyoMeats] = useState(new Set());
  const [myoCheeses, setMyoCheeses] = useState(new Set());
  const [myoToppings, setMyoToppings] = useState(new Set());
  const [myoSauces, setMyoSauces] = useState(new Set());
  const [myoSauceQuantities, setMyoSauceQuantities] = useState({}); // {sauceKey: extra cup quantity}
  const [showExtraSauceCups, setShowExtraSauceCups] = useState(false);
  const [sauceQuantityPopup, setSauceQuantityPopup] = useState(null); // {sauceKey, sauceName}
  const [myoToast, setMyoToast] = useState(''); // 'toasted' | 'not_toasted'
  const [myoDoubleMeat, setMyoDoubleMeat] = useState(false);
  const [myoDoubleCheese, setMyoDoubleCheese] = useState(false);

  // Bag / Cart
  const [bagItems, setBagItems] = useState([]);
  const bagTotal = useMemo(
    () => bagItems.reduce((sum, it) => sum + it.price, 0),
    [bagItems]
  );
  const bagPoints = useMemo(
    () => bagItems.reduce((sum, it) => sum + (it.points || 0), 0),
    [bagItems]
  );

  const [showCart, setShowCart] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  
  // Tip state
  const [tipPercentage, setTipPercentage] = useState(null); // 10, 15, 20, or null for custom
  const [customTipAmount, setCustomTipAmount] = useState("");
  
  // Calculate tip based on selection
  const calculateTip = (subtotal) => {
    if (tipPercentage) {
      return (subtotal * tipPercentage / 100);
    } else if (customTipAmount && !isNaN(parseFloat(customTipAmount))) {
      return (subtotal * parseFloat(customTipAmount) / 100);
    }
    return 0;
  };
  
  const tipAmount = useMemo(() => calculateTip(bagTotal), [bagTotal, tipPercentage, customTipAmount]);
  const roundCurrency = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  
  // Tampa sales tax 7.5% - calculated on subtotal + tip
  const salesTaxRate = 0.075;
  const salesTax = useMemo(() => roundCurrency((bagTotal + tipAmount) * salesTaxRate), [bagTotal, tipAmount]);
  const totalWithTax = useMemo(() => roundCurrency(bagTotal + tipAmount + salesTax), [bagTotal, tipAmount, salesTax]);
  
  const currency = (n) => `$${n.toFixed(2)}`;

  // ====== Premade subs dialog logic ======
  const handleOpen = (sub) => {
    setOpenSub(sub);
    setLengthChoice('12');
    setSelectedExtras({}); // reset extras
    setToastChoice('toasted'); // default to toasted

    setSubMeats(new Set(sub.defaultMeats || []));
    setSubCheeses(new Set(sub.defaultCheeses || []));
    setSubToppings(new Set(sub.defaultToppings || []));
    setSubSauces(new Set(sub.defaultSauces || []));
  };

  const handleClose = () => setOpenSub(null);

  // Helper to toggle ingredient in a Set
  const toggleIngredient = (ingredient, set, setter) => {
    const newSet = new Set(set);
    if (newSet.has(ingredient)) {
      newSet.delete(ingredient);
    } else {
      newSet.add(ingredient);
    }
    setter(newSet);
  };

  const handleLengthToggle = (size) => setLengthChoice(size);

  const handleToggleExtra = (key) =>
    setSelectedExtras((prev) => ({ ...prev, [key]: !prev[key] }));

  const subMeatCount = useMemo(() => subMeats.size, [subMeats]);
  const subDefaultMeatCount = useMemo(() => openSub?.defaultMeats?.length || 0, [openSub]);
  const subOnlyHasDefaultMeats = useMemo(() => {
    if (!openSub?.defaultMeats?.length) return false;
    return Array.from(subMeats).every((meat) => openSub.defaultMeats.includes(meat));
  }, [openSub, subMeats]);
  const includedSubMeatCount = useMemo(
    () => {
      if (openSub?.name === 'Italian' && subOnlyHasDefaultMeats && subMeatCount <= subDefaultMeatCount) {
        return subDefaultMeatCount;
      }
      return 2;
    },
    [openSub, subOnlyHasDefaultMeats, subMeatCount, subDefaultMeatCount]
  );
  const extraSubMeatCount = useMemo(
    () => Math.max(0, subMeatCount - includedSubMeatCount),
    [subMeatCount, includedSubMeatCount]
  );
  const subHasLondonBroil = useMemo(() => subMeats.has('london broil roast beef'), [subMeats]);
  const subHasBacon = useMemo(() => subToppings.has('bacon'), [subToppings]);

  const meatPricingTotal = useMemo(() => {
    let total = 0;

    if (subHasLondonBroil) total += MYO_LONDON_BROIL_UPCHARGE;

    if (extraSubMeatCount > 0) {
      const extraMeatPrice = subHasLondonBroil ? MYO_EXTRA_LB_MEAT : MYO_EXTRA_MEAT;
      total += extraMeatPrice;
    }

    if (subHasBacon) {
      if (lengthChoice === '12') total += MYO_BACON_12;
      else if (lengthChoice === '6') total += MYO_BACON_6;
    }

    return total;
  }, [extraSubMeatCount, subHasLondonBroil, subHasBacon, lengthChoice]);

  const selectedExtrasTotal = useMemo(() => {
    let sum = EXTRA_OPTIONS.reduce(
      (sum, e) => {
        if (!selectedExtras[e.key]) return sum;
        if (e.key === 'doubleMeat' && openSub?.name === 'London Broil') {
          return sum + 3.75;
        }
        return sum + e.price;
      },
      0
    );
    sum += SAUCE_OPTIONS.reduce((acc, sauce) => acc + (selectedExtras[sauce] ? 0.75 : 0), 0);
    return sum;
  }, [selectedExtras, openSub]);

  const dialogPrice = useMemo(() => {
    if (!openSub) return 0;
    const basePrice = lengthChoice === '6' ? openSub.price6 : openSub.price12;
    return basePrice + meatPricingTotal + selectedExtrasTotal;
  }, [openSub, meatPricingTotal, selectedExtrasTotal, lengthChoice]);

  const addToCart = () => {
    if (!openSub) return;
    const ingredientsSelected = [
      lengthChoice === '6' ? '6"' : '12"',
      ...Array.from(subMeats),
      ...Array.from(subCheeses),
      ...Array.from(subToppings),
      ...Array.from(subSauces),
      TOAST_OPTIONS.find((t) => t.key === toastChoice)?.label || toastChoice,
      ...SAUCE_OPTIONS.filter((sauce) => selectedExtras[sauce]).map((sauce) => `Extra ${sauce}`),
    ];
    const extrasApplied = EXTRA_OPTIONS.filter((e) => selectedExtras[e.key]).map((e) => ({
      key: e.key,
      label: e.label,
      price: (e.key === 'doubleMeat' && openSub.name === 'London Broil') ? 3.75 : e.price,
    }));
    if (extraSubMeatCount > 0) {
      const extraMeatPrice = subHasLondonBroil ? MYO_EXTRA_LB_MEAT : MYO_EXTRA_MEAT;
      extrasApplied.push({ key: 'extraMeat', label: `Extra Meat (+${extraSubMeatCount})`, price: extraMeatPrice });
    }
    if (subHasBacon) {
      const baconPrice = lengthChoice === '12' ? MYO_BACON_12 : MYO_BACON_6;
      extrasApplied.push({ key: 'bacon', label: 'Bacon', price: baconPrice });
    }
    SAUCE_OPTIONS.filter((sauce) => selectedExtras[sauce]).forEach((sauce) => {
      extrasApplied.push({ key: sauce, label: sauce, price: 0.75 });
    });
    
    const item = {
      id: `${openSub.name}-${Date.now()}`,
      name: openSub.name,
      img: openSub.img,
      basePrice: lengthChoice === '6' ? openSub.price6 : openSub.price12,
      length: lengthChoice,
      ingredients: ingredientsSelected,
      extras: extrasApplied,
      price: dialogPrice,
      points: lengthChoice === '6' ? openSub.points6 : openSub.points12,
    };
    setBagItems((prev) => [...prev, item]); // +1 to bag
    setOpenSub(null);
  };

  const removeFromCart = (id) => {
    setBagItems((prev) => prev.filter((x) => x.id !== id));
  };

  const sectionStyle = { marginBottom: '30px' };
  const titleStyle = {
    fontFamily: 'FontBold',
    fontSize: '20px',
    color: '#333',
    marginBottom: '10px'
  };

  // Use require for page background (matching your latest)
  const home4Img = require('./home4.jpg');

  // ====== Make Your Own (MYO) derived pricing ======
  const myoHasLondonBroil = useMemo(() => myoMeats.has('London Broil'), [myoMeats]);
  const myoMeatCount = useMemo(() => myoMeats.size, [myoMeats]);
  const myoCheeseCount = useMemo(() => myoCheeses.size, [myoCheeses]);
  const myoHasBacon = useMemo(() => myoToppings.has('Bacon'), [myoToppings]);
  
  const myoTotal = useMemo(() => {
    let total = 0;
    
    // Base price for size
    const sizeObj = MYO_SIZES.find((s) => s.key === myoSize);
    if (sizeObj) total += sizeObj.basePrice;
    
    // London Broil upcharge ($1 if selected)
    if (myoHasLondonBroil) total += MYO_LONDON_BROIL_UPCHARGE;
    
    // 3rd protein charge (2 included, 3rd costs extra)
    if (myoMeatCount > 2) {
      const extraMeatPrice = myoHasLondonBroil ? MYO_EXTRA_LB_MEAT : MYO_EXTRA_MEAT;
      total += extraMeatPrice;
    }
    
    // Double meat
    if (myoDoubleMeat) {
      const doubleMeatPrice = myoHasLondonBroil ? MYO_DOUBLE_MEAT_LB : MYO_DOUBLE_MEAT;
      total += doubleMeatPrice;
    }
    
    // Double cheese
    if (myoDoubleCheese) {
      total += MYO_DOUBLE_CHEESE;
    }
    
    // Bacon
    if (myoHasBacon) {
      if (myoSize === '12') total += MYO_BACON_12;
      else if (myoSize === '6') total += MYO_BACON_6;
    }
    
    // Extra sauce cups are charged separately from sauces on the sandwich.
    const totalSauceCupCount = Object.values(myoSauceQuantities).reduce((sum, qty) => sum + qty, 0);
    if (totalSauceCupCount > 0) {
      total += totalSauceCupCount * MYO_EXTRA_SAUCE;
    }
    
    return +total.toFixed(2);
  }, [myoSize, myoHasLondonBroil, myoMeatCount, myoHasBacon, myoDoubleMeat, myoDoubleCheese, myoSauceQuantities]);

  // toggle helpers (MYO)
  const toggleSet = (set, value, setter) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const resetMYO = () => {
    setMyoSize('');
    setMyoMeats(new Set());
    setMyoCheeses(new Set());
    setMyoToppings(new Set());
    setMyoSauces(new Set());
    setMyoSauceQuantities({});
    setShowExtraSauceCups(false);
    setMyoToast('');
    setMyoDoubleMeat(false);
    setMyoDoubleCheese(false);
  };

  const addMYOToCart = () => {
    // Validation
    if (!myoSize) {
      alert('Please select a size');
      return;
    }
    if (!myoToast) {
      alert('Please select a toasting option');
      return;
    }
    if (myoMeatCount > 3) {
      alert('Maximum 3 proteins allowed');
      return;
    }
    
    // Use custom.png for all custom subs
    let chosenImg = customImg;

    const ingredientLabels = [
      ...Array.from(myoMeats),
      ...Array.from(myoCheeses),
      ...Array.from(myoToppings),
      ...Array.from(myoSauces).map((key) => {
        const sauce = MYO_SAUCES.find((entry) => entry.key === key);
        return sauce ? sauce.label : key;
      }),
      ...Object.entries(myoSauceQuantities).map(([key, qty]) => {
        const s = MYO_SAUCES.find(x => x.key === key);
        const label = s ? s.label : key;
        return `Extra ${label} (x${qty})`;
      }),
      ...(myoToast ? [`Toast: ${MYO_TOAST.find(t => t.key === myoToast)?.label || myoToast}`] : [])
    ];

    const extras = [];
    if (myoHasLondonBroil) extras.push({ key: 'londonBroil', label: 'London Broil Upcharge', price: MYO_LONDON_BROIL_UPCHARGE });
    if (myoMeatCount > 2) {
      const extraMeatPrice = myoHasLondonBroil ? MYO_EXTRA_LB_MEAT : MYO_EXTRA_MEAT;
      extras.push({ key: 'extraMeat', label: '3rd Protein', price: extraMeatPrice });
    }
    if (myoDoubleMeat) {
      const doubleMeatPrice = myoHasLondonBroil ? MYO_DOUBLE_MEAT_LB : MYO_DOUBLE_MEAT;
      extras.push({ key: 'doubleMeat', label: 'Double Meat', price: doubleMeatPrice });
    }
    if (myoDoubleCheese) extras.push({ key: 'doubleCheese', label: 'Double Cheese', price: MYO_DOUBLE_CHEESE });
    if (myoHasBacon) {
      const baconPrice = myoSize === '12' ? MYO_BACON_12 : MYO_BACON_6;
      extras.push({ key: 'bacon', label: 'Bacon', price: baconPrice });
    }
    // Extra sauce cups are a separate paid add-on.
    const totalSauceCupCount = Object.values(myoSauceQuantities).reduce((sum, qty) => sum + qty, 0);
    if (totalSauceCupCount > 0) {
      extras.push({ key: 'extraSauces', label: `Extra Sauce Cups (${totalSauceCupCount})`, price: totalSauceCupCount * MYO_EXTRA_SAUCE });
    }

    const item = {
      id: `Custom Sub-${Date.now()}`,
      name: 'Custom Sub',
      img: chosenImg,
      fileName: 'custom.png',
      basePrice: myoSize === '6' ? MYO_BASE_6 : MYO_BASE_12,
      length: myoSize || '—',
      size: myoSize || '',
      type: 'f',
      ingredients: ingredientLabels,
      extras,
      price: myoTotal,
      points: pointsFromPrice(myoTotal),
    };

    setBagItems((prev) => [...prev, item]); // +1 to bag
    setShowMakeYourOwn(false);
    resetMYO();
  };

  // Render a selectable tile with checkbox (used across MYO lists)
  const SelectTile = ({ checked, label, onClick, rightText, image }) => (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: checked ? '#e0f4ed' : '#f5f5f5',
        borderRadius: 10,
        padding: '10px 12px',
        cursor: 'pointer',
        transition: 'background 0.2s ease'
      }}
    >
      <Checkbox
        checked={checked}
        sx={{ color: '#3AC28E', '&.Mui-checked': { color: '#3AC28E' } }}
      />
      {image ? (
        <img
          src={image}
          alt={label}
          style={{ width: 30, height: 30, objectFit: 'contain', borderRadius: 6 }}
        />
      ) : null}
      <span style={{ fontFamily: 'FontSemi', fontSize: 14 }}>{label}</span>
      {rightText && (
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#666', fontFamily: 'Montserrat, sans-serif' }}>
          {rightText}
        </span>
      )}
    </div>
  );

  const handleAddChip = (chip) => {
    setConfirmAdd({ type: 'chip', item: chip });
  };
  
  const handleAddDrink = (drink) => {
    setConfirmAdd({ type: 'drink', item: drink });
  };
  

  return (
    <div
      style={{
        backgroundImage: `url(${home4Img})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '40px'
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
          width: '90%',
          maxWidth: '900px',
          padding: '30px'
        }}
      >
              <div
                style={{
                  background: '#fff3cd',
                  border: '1px solid #f0d37a',
                  borderRadius: 18,
                  padding: '14px 18px',
                  marginBottom: 20,
                  color: '#5f4600',
                  fontFamily: 'FontSemi',
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 700,
                  textAlign: 'center'
                }}
              >
                {orderingDisabledMessage}
              </div>
              <div
                style={{
                  background: 'linear-gradient(90deg, #222 0%, #3AC28E 100%)',
                  borderRadius: 40,
                  padding: '18px 28px',
                  marginBottom: 28,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 22,
                  boxShadow: '0 4px 16px rgba(60,194,142,0.10)',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 17,
                  fontWeight: 500,
                }}
              >
                <img
                  src={appIcon}
                  alt="App Icon"
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 12,
                    marginRight: 5,
                    objectFit: 'cover'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, letterSpacing: 0.2 }}>
                    Download our app!
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 16 }}>
                    <li style={{ marginBottom: 6 }}>• Earn points on every order</li>
                    <li style={{ marginBottom: 6 }}>• Get exclusive offers & rewards</li>
                    <li style={{ marginBottom: 6 }}>• Order pickup with a few clicks</li>
                    <li>• Catering orders are available in the mobile app or by calling the store</li>
                  </ul>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <a
                    href="https://apps.apple.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: '#fff',
                      color: '#222',
                      fontWeight: 600,
                      borderRadius: 14,
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: 15,
                      padding: '8px 18px',
                      boxShadow: 'none',
                      textTransform: 'none',
                      border: '2px solid #222',
                      textDecoration: 'none',
                      marginBottom: 2,
                      transition: 'background 0.2s, color 0.2s, border 0.2s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = '#111';
                      e.currentTarget.style.color = '#fff';
                      e.currentTarget.style.border = '2px solid #111';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.color = '#222';
                      e.currentTarget.style.border = '2px solid #222';
                    }}
                  >
                    iPhone
                  </a>
                  <a
                    href="https://play.google.com/store"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: '#fff',
                      color: '#222',
                      fontWeight: 600,
                      borderRadius: 14,
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: 15,
                      padding: '8px 18px',
                      boxShadow: 'none',
                      textTransform: 'none',
                      border: '2px solid #222',
                      textDecoration: 'none',
                      transition: 'background 0.2s, color 0.2s, border 0.2s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = '#111';
                      e.currentTarget.style.color = '#fff';
                      e.currentTarget.style.border = '2px solid #111';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.color = '#222';
                      e.currentTarget.style.border = '2px solid #222';
                    }}
                  >
                    Android
                  </a>
                </div>
              </div>
        {/* Bag summary (clickable) */}
        <div
          onClick={() => setShowCart(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
            cursor: 'pointer'
          }}

        >
          
          <h2
            style={{
              fontFamily: 'FontBold',
              color: '#2e2e2e',
              margin: 0,
            
            }}
          >
            Pickup Order
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LocalMallIcon style={{ color: '#3AC28E', fontSize: 28 }} />
            <span
              style={{
                fontFamily: 'FontSemi',
                fontWeight: 600,
                color: '#333'
              }}
            >
              {bagItems.length} item{bagItems.length !== 1 ? 's' : ''} | {currency(bagTotal)}
            </span>
          </div>
        </div>



        {/* Make Your Own pill (left-aligned, 50% width) */}
        <div
          onClick={() => setShowMakeYourOwn(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            borderRadius: '40px',
            boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
            padding: '0 0 0 20px',
            marginBottom: '20px',
            overflow: 'hidden',
            cursor: 'pointer',
            height: '80px',


          }}
        >
          <h3
            style={{
              margin: 0,
              fontFamily: 'FontSemi',
              color: '#2e2e2e',
              fontSize: '17px',
              flex: 1
            }}
          >
            Make Your Own!
          </h3>
          <img
            src={homeImg}
            alt="Make your own"
            style={{
              width: 170,
              height: '100%',
              objectFit: 'cover',
              borderTopRightRadius: '40px',
              borderBottomRightRadius: '40px'
            }}
          />
        </div>

        {/* Subs Section */}
        <div style={sectionStyle}>
          <p style={titleStyle}>Subs</p>
          {subs.map((sub, index) => (
            <div
              key={index}
              onClick={() => handleOpen(sub)} // Make entire sub clickable
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '15px',
                backgroundColor: '#fafafa',
                padding: '12px 16px',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fafafa')}
            >
              <img
                src={sub.img}
                alt={sub.name}
                style={{
                  width: 150,
                  height: 100,
                  borderRadius: '10px',
                  objectFit: 'contain',
                  marginRight: '15px'
                }}
              />
              <div style={{ flex: 1 }}>
                <span
                  style={{
                    fontFamily: 'FontSemi',
                    fontSize: '17px',
                    color: '#222'
                  }}
                >
                  {sub.name}
                </span>
                <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
                  from {currency(sub.price6)}
                </p>
              </div>
              <Tooltip title="Customize & add">
                <IconButton onClick={(e) => { e.stopPropagation(); handleOpen(sub); }}>
                  <ShoppingBagIcon style={{ color: '#3AC28E', fontSize: 30 }} />
                </IconButton>
              </Tooltip>
            </div>
          ))}

        </div>

        {/* Chips Section */}
        <div style={sectionStyle}>
          <h3 style={titleStyle}>Chips & Cookies</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: '12px'
            }}
          >
            {chips.map((chip, index) => (
              <div
                key={index}
                onClick={() => handleAddChip(chip)}
                style={{
                  backgroundColor: '#fafafa',
                  borderRadius: '12px',
                  textAlign: 'center',
                  padding: '10px',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fafafa')}
              >

                <img
                  src={chip.img}
                  alt={chip.name}
                  style={{
                    width: '100%',
                    height: 110,
                    objectFit: 'contain',
                    borderRadius: '8px',

                  }}
                />
                <p
                  style={{
                    marginTop: '6px',
                    fontFamily: 'FontSemi',
                    fontSize: '14px',
                    color: '#222'
                  }}
                >
                  {chip.name}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                  {currency(chip.price)}
                </p>
              </div>
            )
            )}
          </div>
        </div>

        {/* Drinks Section */}
        <div style={sectionStyle}>
          <h3 style={titleStyle}>Drinks</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: '12px'
            }}
          >
            {drinks.map((drink, index) => (
              <div
                key={index}
                onClick={() => handleAddDrink(drink)}
                style={{
                  backgroundColor: '#fafafa',
                  borderRadius: '12px',
                  textAlign: 'center',
                  padding: '10px',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fafafa')}
              >

                <img
                  src={drink.img}
                  alt={drink.name}
                  style={{
                    width: '100%',
                    height: 110,
                    objectFit: 'contain',
                    borderRadius: '8px',

                  }}
                />
                <p
                  style={{
                    marginTop: '6px',
                    fontFamily: 'FontSemi',
                    fontSize: '14px',
                    color: '#222'
                  }}
                >
                  {drink.name}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                  {currency(drink.price)}
                </p>
              </div>
            ))}
          </div>
          {/* Proceed to Checkout button at bottom */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 30 }}>
            <Button
              variant="contained"
              disabled
              style={{
                backgroundColor: '#8aa89b',
                color: 'white',
                textTransform: 'none',
                fontFamily: 'FontSemi',
                fontSize: 18,
                borderRadius: 12,
                padding: '12px 40px',
                boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                cursor: 'not-allowed',
                opacity: 1,
              }}
            >
              Proceed to Checkout Disabled: {currency(bagTotal)}
            </Button>
          </div>

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link
              to="/privacy"
              style={{
                color: '#3AC28E',
                fontFamily: 'FontSemi',
                fontSize: '0.95rem',
                textDecoration: 'none'
              }}
            >
              Privacy Policy
            </Link>
          </div>

        </div>

      </div>

      {/* Premade Sub Customization Modal */}
      <Dialog open={!!openSub} onClose={handleClose} fullWidth maxWidth="md">
        {openSub && (
          <>
            {/* Black Header */}
            <div
              style={{
                backgroundColor: 'black',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '15px 20px'
              }}
            >
              <div style={{ flex: 1, paddingRight: 12 }}>
                <h3
                  style={{
                    fontFamily: 'FontSemi',
                    margin: 0,
                    fontSize: '20px'
                  }}
                >
                  {openSub.name}
                </h3>
                <p
                  style={{
                    fontFamily: 'FontSemi',
                    fontSize: '14px',
                    color: '#ccc',
                    marginTop: '4px'
                  }}
                >
                  {ingredientMap[openSub.name]?.desc}
                </p>
              </div>
              <img
                src={openSub.img}
                alt={openSub.name}
                style={{
                  width: 120,
                  height: 80,
                  objectFit: 'contain',
                  borderRadius: '10px'
                }}
              />
            </div>

            <DialogContent style={{ paddingTop: '15px' }}>
              {/* Length (left-aligned at top) */}
              <div style={{ marginBottom: 16 }}>
                <h4
                  style={{
                    fontFamily: 'FontSemi',
                    margin: 0,
                    marginBottom: 8,
                    color: '#333'
                  }}
                >
                  Choose Length
                </h4>
                <div style={{ display: 'flex', gap: 16 }}>
                  {['6', '12'].map((size) => (
                    <div
                      key={size}
                      onClick={() => handleLengthToggle(size)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        backgroundColor:
                          lengthChoice === size ? '#e0f4ed' : '#f5f5f5',
                        borderRadius: '10px',
                        padding: '8px 15px'
                      }}
                    >
                      <Checkbox
                        checked={lengthChoice === size}
                        sx={{
                          color: '#3AC28E',
                          '&.Mui-checked': { color: '#3AC28E' }
                        }}
                      />
                      <span
                        style={{
                          fontFamily: 'FontSemi',
                          fontSize: '15px'
                        }}
                      >
                        {size}"
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ingredients */}
              <h4
                style={{
                  fontFamily: 'FontSemi',
                  margin: 0,
                  marginBottom: 10,
                  color: '#333'
                }}
              >
                Ingredients
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px',
                  marginBottom: 18
                }}
              >
                {/* Meats */}
                {MYO_MEATS.map((meat) => (
                  <SelectTile
                    key={meat.label}
                    checked={subMeats.has(meat.label)}
                    label={meat.label}
                    image={meat.img}
                    onClick={() => toggleIngredient(meat.label, subMeats, setSubMeats)}
                  />
                ))}
              </div>

              <h4
                style={{
                  fontFamily: 'FontSemi',
                  margin: 0,
                  marginBottom: 10,
                  color: '#333'
                }}
              >
                Cheeses
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px',
                  marginBottom: 18
                }}
              >
                {MYO_CHEESES.map((cheese) => (
                  <SelectTile
                    key={cheese.label}
                    checked={subCheeses.has(cheese.label)}
                    label={cheese.label}
                    image={cheese.img}
                    onClick={() => toggleIngredient(cheese.label, subCheeses, setSubCheeses)}
                  />
                ))}
              </div>

              <h4
                style={{
                  fontFamily: 'FontSemi',
                  margin: 0,
                  marginBottom: 10,
                  color: '#333'
                }}
              >
                Toppings
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px',
                  marginBottom: 18
                }}
              >
                {MYO_TOPPINGS.map((topping) => (
                  <SelectTile
                    key={topping.label}
                    checked={subToppings.has(topping.label)}
                    label={topping.label}
                    image={topping.img}
                    onClick={() => toggleIngredient(topping.label, subToppings, setSubToppings)}
                  />
                ))}
              </div>

              <h4
                style={{
                  fontFamily: 'FontSemi',
                  margin: 0,
                  marginBottom: 10,
                  color: '#333'
                }}
              >
                Sauces
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px',
                  marginBottom: 18
                }}
              >
                {MYO_SAUCES.map((sauce) => (
                  <SelectTile
                    key={sauce.key}
                    checked={subSauces.has(sauce.label)}
                    label={sauce.label}
                    image={sauce.img}
                    onClick={() => toggleIngredient(sauce.label, subSauces, setSubSauces)}
                  />
                ))}
              </div>

              {/* Toast Choice */}
              <h4
                style={{
                  fontFamily: 'FontSemi',
                  margin: 0,
                  marginBottom: 10,
                  color: '#333',
                  marginTop: 18
                }}
              >
                Toast
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 18 }}>
                {MYO_TOAST.map((toast) => (
                  <div
                    key={toast.key}
                    onClick={() => setToastChoice(toast.key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      backgroundColor: toastChoice === toast.key ? '#e0f4ed' : '#f5f5f5',
                      borderRadius: 10,
                      padding: '8px 10px',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                  >
                    <Checkbox
                      checked={toastChoice === toast.key}
                      sx={{ color: '#3AC28E', '&.Mui-checked': { color: '#3AC28E' } }}
                    />
                    <span style={{ fontFamily: 'FontSemi', fontSize: 14 }}>
                      {toast.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Extras with costs */}
              <h4
                style={{
                  fontFamily: 'FontSemi',
                  margin: 0,
                  marginBottom: 10,
                  color: '#333'
                }}
              >
                Extras
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {EXTRA_OPTIONS.map((e) => (
                  <div
                    key={e.key}
                    onClick={() => handleToggleExtra(e.key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      backgroundColor: selectedExtras[e.key] ? '#e0f4ed' : '#f5f5f5',
                      borderRadius: 10,
                      padding: '8px 10px',
                      cursor: 'pointer'
                    }}
                  >
                    <Checkbox
                      checked={!!selectedExtras[e.key]}
                      sx={{ color: '#3AC28E', '&.Mui-checked': { color: '#3AC28E' } }}
                    />
                    <span style={{ fontFamily: 'FontSemi', fontSize: 14 }}>
                      {e.label} <span style={{ color: '#666' }}>+{currency(e.price)}</span>
                    </span>
                  </div>
                ))}
              </div>

              {/* Add to Cart */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 22 }}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  startIcon={<CloseIcon />}
                  style={{
                    borderColor: '#bbb',
                    color: '#333',
                    textTransform: 'none',
                    borderRadius: 12,
                    padding: '10px 16px',
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={addToCart}
                  style={{
                    backgroundColor: '#3AC28E',
                    fontFamily: 'FontSemi',
                    textTransform: 'none',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    fontSize: '16px'
                  }}
                >
                  Add to Cart: {currency(dialogPrice)}
                </Button>
              </div>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Cart Modal */}
      <Dialog open={showCart} onClose={() => setShowCart(false)} fullWidth maxWidth="sm">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 18px',
            borderBottom: '1px solid #eee',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LocalMallIcon style={{ color: '#3AC28E' }} />
            <h3 style={{ margin: 0, fontFamily: 'Montserrat, sans-serif' }}>Your Bag</h3>
          </div>
          <IconButton onClick={() => setShowCart(false)} size="small">
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent style={{ paddingTop: 16 }}>
          {bagItems.length === 0 ? (
            <p style={{ fontFamily: 'FontSemi', color: '#555', textAlign: 'center' }}>
              Your bag is empty.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {bagItems.map((it) => (
                <div
                  key={it.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 1fr auto',
                    gap: 12,
                    alignItems: 'center',
                    backgroundColor: '#fafafa',
                    borderRadius: 12,
                    padding: 10
                  }}
                >
                  <img
                    src={it.img}
                    alt={it.name}
                    style={{
                      width: 80,
                      height: 60,
                      objectFit: 'contain',
                      borderRadius: 8,

                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong style={{ fontFamily: 'Montserrat, sans-serif' }}>{it.name}</strong>
                      {it.length && it.length !== '—' && (
                        <span style={{ fontSize: 12, color: '#666', fontFamily: 'Montserrat, sans-serif' }}>
                          • {it.length}"
                        </span>
                      )}

                    </div>
                    {it.extras?.length > 0 && (
                      <div style={{ fontSize: 13, color: '#333', fontFamily: 'Montserrat, sans-serif' }}>
                        Extras: {it.extras.map((e) => `${e.label} (+${currency(e.price)})`).join(', ')}
                      </div>
                    )}
                    {it.ingredients?.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 6,
                          marginTop: 4
                        }}
                      >
                        {it.ingredients.map((ing) => (
                          <span
                            key={ing}
                            style={{
                              fontSize: 12,
                              padding: '4px 8px',
                              background: '#f0f0f0',
                              borderRadius: 10,
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {ing}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'FontSemi', fontWeight: 600 }}>
                      {currency(it.price)}
                    </span>
                    <IconButton onClick={() => removeFromCart(it.id)} size="small">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </div>
                </div>
              ))}


              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px solid #eee'
                }}
              >
                <strong style={{ fontFamily: 'Montserrat, sans-serif' }}>Subtotal</strong>
                <strong style={{ fontFamily: 'Montserrat, sans-serif' }}>{currency(bagTotal)}</strong>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 4,
                }}
              >
                <span style={{ fontFamily: 'Montserrat, sans-serif', color: '#666' }}>Sales Tax (7.5%)</span>
                <span style={{ fontFamily: 'Montserrat, sans-serif', color: '#666' }}>{currency(salesTax)}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '2px solid #eee'
                }}
              >
                <strong style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 18 }}>Total</strong>
                <strong style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 18, color: '#3AC28E' }}>
                  {currency(totalWithTax)}
                </strong>
              </div>

              <Button
                variant="contained"
                fullWidth
                disabled
                style={{
                  backgroundColor: '#8aa89b',
                  textTransform: 'none',
                  borderRadius: 12,
                  padding: '12px 16px',
                  fontFamily: 'FontSemi',
                  fontSize: 16,
                  marginTop: 4,
                  cursor: 'not-allowed',
                  opacity: 1,
                }}
              >
                Checkout Disabled
              </Button>
                  {/* Payment Modal */}
                  <Dialog open={showPayment} onClose={() => {
                    setShowPayment(false);
                    setTipPercentage(null);
                    setCustomTipAmount('');
                  }} fullWidth maxWidth="sm">
                    <div style={{ padding: 24 }}>
                      <h2 style={{ fontFamily: 'FontSemi', marginBottom: 16 }}>Payment</h2>
                      {/* Unified Promo Section with three bullets and a title */}
                      <div style={{
                        background: 'linear-gradient(90deg, #222 0%, #3AC28E 100%)',
                        borderRadius: 14,
                        padding: '22px 24px',
                        marginBottom: 22,
                        color: 'white',
                        boxShadow: '0 4px 16px rgba(60,194,142,0.10)',
                        fontSize: 17,
                        fontWeight: 500,
                      }}>
                        <div style={{ fontSize: 20, fontFamily: 'FontBold', marginBottom: 14, letterSpacing: 0.2 }}>Download our app!</div>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                            <StarIcon style={{ fontSize: 22, marginRight: 10, color: 'white' }} />
                            Catering orders are available in the mobile app or by calling the store.
                          </li>
                          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                            <CreditCardIcon style={{ fontSize: 22, marginRight: 10, color: 'white', fontFamily: 'FontBold', }} />
                            Save your card details and streamline checkout!
                          </li>
                          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                            <StarIcon style={{ fontSize: 22, marginRight: 10, color: 'white' }} />
                            Earn rewards and get exclusive offers in the app!
                          </li>
                          <li style={{ display: 'flex', alignItems: 'center' }}>
                            <StarIcon style={{ fontSize: 22, marginRight: 10, color: 'white' }} />
                            Order catering in the app and skip the phone call while tracking your catering order.
                          </li>
                        </ul>
                        <div style={{ display: 'flex', gap: isMobile ? 8 : 16, marginTop: 18, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                          <Button
                            variant="contained"
                            startIcon={<img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" alt="Apple" style={{ width: 20, height: 20 }} />}
                            style={{
                              background: '#fff',
                              color: '#222',
                              fontWeight: 600,
                              borderRadius: 10,
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: isMobile ? 12 : 15,
                              padding: isMobile ? '8px 12px' : '10px 22px',
                              boxShadow: 'none',
                              textTransform: 'none',
                              border: '2px solid #222',
                              transition: 'background 0.2s, color 0.2s, border 0.2s',
                              whiteSpace: 'nowrap',
                            }}
                            href="https://apps.apple.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            onMouseEnter={e => {
                              e.currentTarget.style.background = '#111';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.border = '2px solid #111';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = '#fff';
                              e.currentTarget.style.color = '#222';
                              e.currentTarget.style.border = '2px solid #222';
                            }}
                          >
                            Download on iPhone
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<img src="https://upload.wikimedia.org/wikipedia/commons/d/d7/Android_robot.svg" alt="Android" style={{ width: 20, height: 20 }} />}
                            style={{
                              background: '#fff',
                              color: '#222',
                              fontWeight: 600,
                              borderRadius: 10,
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: isMobile ? 12 : 15,
                              padding: isMobile ? '8px 12px' : '10px 22px',
                              boxShadow: 'none',
                              textTransform: 'none',
                              border: '2px solid #222',
                              transition: 'background 0.2s, color 0.2s, border 0.2s',
                              whiteSpace: 'nowrap',
                            }}
                            href="https://play.google.com/store"
                            target="_blank"
                            rel="noopener noreferrer"
                            onMouseEnter={e => {
                              e.currentTarget.style.background = '#111';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.border = '2px solid #111';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = '#fff';
                              e.currentTarget.style.color = '#222';
                              e.currentTarget.style.border = '2px solid #222';
                            }}
                          >
                            Download on Android
                          </Button>
                        </div>
                      </div>
                                              <hr style={{ border: 'none', borderTop: '1.5px solid #e0e0e0', margin: '24px 0 24px 0' }} />
                      <div style={{ width: '100%', display: 'block', margin: 0, padding: 0 }}>
                        <div style={{ fontFamily: 'FontSemi', fontSize: 22, color: '#111', marginBottom: 2, fontWeight: 700, letterSpacing: 0.2 }}>
                          Checkout Details
                        </div>
                        <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 15, color: '#444', marginBottom: 20, marginTop: 2 }}>
                          Enter your name, email, and card details below to complete your order.
                        </div>
                        {/* First Name and Email fields */}
                        <div style={{ width: '80%', margin: '0 0 18px 0', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
                          <input
                            type="text"
                            placeholder="First Name"
                            value={paymentName}
                            onChange={e => setPaymentName(e.target.value)}
                            style={{
                              width: '75%',
                              padding: '10px 12px',
                              border: '1px solid #ddd',
                              borderRadius: 8,
                              fontSize: 15,
                              fontFamily: 'Montserrat, sans-serif',
                            }}
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            value={paymentEmail}
                            onChange={e => setPaymentEmail(e.target.value)}
                            style={{
                              width: '75%',
                              padding: '10px 12px',
                              border: '1px solid #ddd',
                              borderRadius: 8,
                              fontSize: 15,
                              fontFamily: 'Montserrat, sans-serif',
                            }}
                          />
                          
                          {/* Tip Section */}
                          <div style={{ width: '75%', marginTop: 8 }}>
                            <label style={{ fontFamily: 'FontSemi', fontSize: 15, color: '#333', display: 'block', marginBottom: 8 }}>
                              Add a Tip (Optional)
                            </label>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                              <button
                                onClick={() => {
                                  setTipPercentage(10);
                                  setCustomTipAmount('');
                                }}
                                style={{
                                  padding: '8px 16px',
                                  border: tipPercentage === 10 ? '2px solid #3AC28E' : '1px solid #ddd',
                                  borderRadius: 8,
                                  backgroundColor: tipPercentage === 10 ? '#e0f4ed' : 'white',
                                  color: tipPercentage === 10 ? '#3AC28E' : '#333',
                                  fontFamily: 'Montserrat, sans-serif',
                                  fontSize: 14,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                              >
                                10%
                              </button>
                              <button
                                onClick={() => {
                                  setTipPercentage(15);
                                  setCustomTipAmount('');
                                }}
                                style={{
                                  padding: '8px 16px',
                                  border: tipPercentage === 15 ? '2px solid #3AC28E' : '1px solid #ddd',
                                  borderRadius: 8,
                                  backgroundColor: tipPercentage === 15 ? '#e0f4ed' : 'white',
                                  color: tipPercentage === 15 ? '#3AC28E' : '#333',
                                  fontFamily: 'Montserrat, sans-serif',
                                  fontSize: 14,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                              >
                                15%
                              </button>
                              <button
                                onClick={() => {
                                  setTipPercentage(20);
                                  setCustomTipAmount('');
                                }}
                                style={{
                                  padding: '8px 16px',
                                  border: tipPercentage === 20 ? '2px solid #3AC28E' : '1px solid #ddd',
                                  borderRadius: 8,
                                  backgroundColor: tipPercentage === 20 ? '#e0f4ed' : 'white',
                                  color: tipPercentage === 20 ? '#3AC28E' : '#333',
                                  fontFamily: 'Montserrat, sans-serif',
                                  fontSize: 14,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                              >
                                20%
                              </button>
                              <button
                                onClick={() => {
                                  setTipPercentage(null);
                                  if (!customTipAmount) setCustomTipAmount('5');
                                }}
                                style={{
                                  padding: '8px 16px',
                                  border: tipPercentage === null && customTipAmount ? '2px solid #3AC28E' : '1px solid #ddd',
                                  borderRadius: 8,
                                  backgroundColor: tipPercentage === null && customTipAmount ? '#e0f4ed' : 'white',
                                  color: tipPercentage === null && customTipAmount ? '#3AC28E' : '#333',
                                  fontFamily: 'Montserrat, sans-serif',
                                  fontSize: 14,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                              >
                                Other
                              </button>
                            </div>
                            {tipPercentage === null && (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                  <span style={{ fontSize: 13, color: '#666', fontFamily: 'Montserrat, sans-serif' }}>Custom Tip</span>
                                  <span style={{ fontSize: 15, fontWeight: 600, color: '#3AC28E', fontFamily: 'Montserrat, sans-serif' }}>{customTipAmount || 0}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="30"
                                  step="1"
                                  value={customTipAmount || 0}
                                  onChange={e => setCustomTipAmount(e.target.value)}
                                  style={{
                                    width: '100%',
                                    height: 6,
                                    borderRadius: 3,
                                    outline: 'none',
                                    WebkitAppearance: 'none',
                                    background: `linear-gradient(to right, #3AC28E 0%, #3AC28E ${(customTipAmount || 0) * 100 / 30}%, #ddd ${(customTipAmount || 0) * 100 / 30}%, #ddd 100%)`,
                                    cursor: 'pointer'
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          
                          {/* Order Summary */}
                          <div style={{ width: '75%', marginTop: 24, padding: '16px', backgroundColor: '#f9f9f9', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                            <div style={{ fontFamily: 'FontSemi', fontSize: 16, color: '#111', marginBottom: 12 }}>
                              Order Summary
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                              <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 15, color: '#333' }}>Subtotal</span>
                              <span style={{ fontFamily: 'FontSemi', fontSize: 15, color: '#3AC28E' }}>
                                {currency(bagTotal)}
                              </span>
                            </div>
                            
                            {(tipPercentage || customTipAmount) && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 15, color: '#333' }}>
                                  Tip ({tipPercentage || customTipAmount}%)
                                </span>
                                <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 15, color: '#333' }}>
                                  {currency(tipAmount)}
                                </span>
                              </div>
                            )}
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                              <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 15, color: '#333' }}>Sales Tax (7.5%)</span>
                              <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 15, color: '#333' }}>
                                {currency(salesTax)}
                              </span>
                            </div>
                            
                            <div style={{ borderTop: '2px solid #ddd', paddingTop: 12, marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontFamily: 'FontSemi', fontSize: 18, color: '#111', fontWeight: 700 }}>Total</span>
                              <span style={{ fontFamily: 'FontSemi', fontSize: 18, color: '#111', fontWeight: 700 }}>
                                {currency(totalWithTax)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div style={{ width: '100%' }}>
                          <ClientPaymentForm
                            amount={Math.round(totalWithTax * 100)}
                            name={paymentName}
                            email={paymentEmail}
                            items={bagItems}
                            totalItems={bagItems.length}
                            subtotal={Math.round(bagTotal * 100)}
                            totalPrice={Math.round(bagTotal * 100)}
                            salesTax={Math.round(salesTax * 100)}
                            tip={Math.round(tipAmount * 100)}
                            deliveryFee={0}
                            finalTotal={Math.round(totalWithTax * 100)}
                            totalPoints={bagPoints}
                            status="pending"
                            orderType="standard"
                            location="West Shore"
                            pickupTime={undefined}
                            eventDate={undefined}
                            payloadOverrides={{
                              origin: 'website',
                              email: paymentEmail.trim(),
                              userEmail: paymentEmail.trim(),
                              status: 'pending',
                            }}
                            onSuccess={() => {
                              setShowPayment(false);
                              setTipPercentage(null);
                              setCustomTipAmount('');
                              setShowCart(false);
                              setBagItems([]);
                              // Generate order number and ready time
                              const orderNum = Math.floor(100000 + Math.random() * 900000).toString();
                              setOrderNumber(orderNum);
                              const now = new Date();
                              now.setMinutes(now.getMinutes() + 25);
                              const ready = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              setReadyTime(ready);
                              setShowSuccess(true);
                            }}
                            onError={(err) => {
                              // Optionally show error message
                              console.error(err);
                            }}
                          />
                              {/* Success Dialog */}
                              <Dialog open={showSuccess} onClose={() => setShowSuccess(false)} maxWidth="xs" fullWidth>
                                <div style={{ textAlign: 'center', padding: '36px 20px' }}>
                                  <h2 style={{ fontFamily: 'FontSemi', color: '#3AC28E', marginBottom: 12 }}>Order Confirmed!</h2>
                                  <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 18, marginBottom: 10 }}>
                                    Your order number is <span style={{ fontWeight: 700 }}>{orderNumber}</span>
                                  </div>
                                  <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 16, color: '#444', marginBottom: 18 }}>
                                    It will be ready for pickup at <span style={{ fontWeight: 600 }}>{readyTime}</span>
                                  </div>
                                  <Button
                                    variant="contained"
                                    style={{ backgroundColor: '#3AC28E', color: 'white', borderRadius: 10, fontFamily: 'FontSemi', fontSize: 16, padding: '10px 30px' }}
                                    onClick={() => setShowSuccess(false)}
                                  >
                                    Close
                                  </Button>
                                </div>
                              </Dialog>
                        </div>
                      </div>
                    </div>
                  </Dialog>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Make Your Own Popup (black header) */}
      <Dialog
        open={showMakeYourOwn}
        onClose={() => {
          setShowMakeYourOwn(false);
          resetMYO();
        }}
        fullWidth
        maxWidth="md"
      >
        <div
          style={{
            backgroundColor: 'black',
            color: 'white',
            padding: '15px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h3 style={{ margin: 0, fontFamily: 'Montserrat, sans-serif' }}>
            Build Your Own Sub
          </h3>
          <IconButton
            onClick={() => {
              setShowMakeYourOwn(false);
              resetMYO();
            }}
            style={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent style={{ paddingTop: 16 }}>
          {/* Rules Section */}
          <div style={{ 
            backgroundColor: '#f0f9f5', 
            borderLeft: '4px solid #3AC28E', 
            padding: '12px 16px', 
            marginBottom: 20, 
            borderRadius: 8 
          }}>
            <h4 style={{ fontFamily: 'FontSemi', margin: '0 0 8px 0', color: '#333', fontSize: 16 }}>
              Build Your Own Rules
            </h4>
            <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, color: '#555', lineHeight: 1.6 }}>
              <div style={{ marginBottom: 4 }}>• Base prices: 6" ${MYO_BASE_6.toFixed(2)}, 12" ${MYO_BASE_12.toFixed(2)}</div>
              <div style={{ marginBottom: 4 }}>• Two proteins included (3rd protein +${MYO_EXTRA_MEAT.toFixed(2)}, or +${MYO_EXTRA_LB_MEAT.toFixed(2)} if London Broil)</div>
              <div style={{ marginBottom: 4 }}>• London Broil has an extra ${MYO_LONDON_BROIL_UPCHARGE.toFixed(2)} upcharge</div>
              <div style={{ marginBottom: 4 }}>• Select up to 3 cheeses per sub (included)</div>
              <div style={{ marginBottom: 4 }}>• Bacon: ${MYO_BACON_6.toFixed(2)} for 6", ${MYO_BACON_12.toFixed(2)} for 12"</div>
              <div style={{ marginBottom: 4 }}>• Double meat: ${MYO_DOUBLE_MEAT.toFixed(2)} standard, ${MYO_DOUBLE_MEAT_LB.toFixed(2)} with London Broil</div>
              <div style={{ marginBottom: 4 }}>• Double cheese: ${MYO_DOUBLE_CHEESE.toFixed(2)}</div>
              <div>• Extra sauces: ${MYO_EXTRA_SAUCE.toFixed(2)} each</div>
            </div>
          </div>

          {/* Size */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontFamily: 'FontSemi', margin: 0, marginBottom: 8, color: '#333' }}>
              Choose Size
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {MYO_SIZES.map((s) => (
                <SelectTile
                  key={s.key}
                  checked={myoSize === s.key}
                  label={`${s.label}`}
                  onClick={() => setMyoSize(myoSize === s.key ? '' : s.key)}
                />
              ))}
            </div>
          </div>

          {/* Meats */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontFamily: 'FontSemi', margin: 0, marginBottom: 8, color: '#333' }}>
              Meats
            </h4>
            {myoMeatCount > 3 && (
              <div style={{ color: '#FF6B6B', fontSize: 12, marginBottom: 6, fontFamily: 'Montserrat, sans-serif' }}>
                ⚠ Maximum 3 proteins allowed
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {MYO_MEATS.map((m) => {
                const isSelected = myoMeats.has(m.label);
                let priceText = undefined;
                
                // Only show price if not selected
                if (!isSelected) {
                  if (myoMeatCount >= 2) {
                    // Show 3rd protein pricing for all meats
                    priceText = m.label === 'London Broil' 
                      ? `+$${MYO_EXTRA_LB_MEAT.toFixed(2)}` 
                      : `+$${MYO_EXTRA_MEAT.toFixed(2)}`;
                  } else if (m.label === 'London Broil') {
                    // Only London Broil shows upcharge when less than 2 selected
                    priceText = `+$${MYO_LONDON_BROIL_UPCHARGE.toFixed(2)}`;
                  }
                }
                
                return (
                  <SelectTile
                    key={m.label}
                    checked={isSelected}
                    label={m.label}
                    image={m.img}
                    rightText={priceText}
                    onClick={() => {
                      if (!isSelected && myoMeatCount >= 3) return;
                      toggleSet(myoMeats, m.label, setMyoMeats);
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Cheeses */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontFamily: 'FontSemi', margin: 0, marginBottom: 8, color: '#333' }}>
              Cheeses
            </h4>
            {myoCheeseCount > 3 && (
              <div style={{ color: '#FF6B6B', fontSize: 12, marginBottom: 6, fontFamily: 'Montserrat, sans-serif' }}>
                ⚠ Maximum 3 cheeses recommended
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {MYO_CHEESES.map((c) => (
                <SelectTile
                  key={c.label}
                  checked={myoCheeses.has(c.label)}
                  label={c.label}
                  image={c.img}
                  onClick={() => toggleSet(myoCheeses, c.label, setMyoCheeses)}
                />
              ))}
            </div>
          </div>

          {/* Toppings */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontFamily: 'FontSemi', margin: 0, marginBottom: 8, color: '#333' }}>
              Toppings
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {MYO_TOPPINGS.map((t) => (
                <SelectTile
                  key={t.label}
                  checked={myoToppings.has(t.label)}
                  label={t.label}
                  image={t.img}
                  rightText={t.label === 'Bacon' ? (myoSize === '12' ? `+$${MYO_BACON_12.toFixed(2)}` : myoSize === '6' ? `+$${MYO_BACON_6.toFixed(2)}` : `+$${MYO_BACON_6.toFixed(2)} / +$${MYO_BACON_12.toFixed(2)}`) : undefined}
                  onClick={() => toggleSet(myoToppings, t.label, setMyoToppings)}
                />
              ))}
            </div>
          </div>

          {/* Sauces on the sandwich */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontFamily: 'FontSemi', margin: 0, marginBottom: 8, color: '#333' }}>
              Sauces on Sub
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {MYO_SAUCES.map((s) => {
                const checked = myoSauces.has(s.key);
                return (
                  <SelectTile
                    key={s.key}
                    checked={checked}
                    label={s.label}
                    image={s.img}
                    onClick={() => toggleSet(myoSauces, s.key, setMyoSauces)}
                  />
                );
              })}
            </div>
          </div>

          {/* Extra sauce cups */}
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => setShowExtraSauceCups((prev) => !prev)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#f5f5f5',
                border: 'none',
                borderRadius: 10,
                padding: '12px 14px',
                cursor: 'pointer',
                fontFamily: 'FontSemi',
                fontSize: 15,
                color: '#333'
              }}
            >
              <span>Extra Sauce Cups (+${MYO_EXTRA_SAUCE.toFixed(2)} each)</span>
              {showExtraSauceCups ? <ExpandLess /> : <ExpandMore />}
            </button>

            {showExtraSauceCups && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 12 }}>
                {MYO_SAUCES.map((s) => {
                  const quantity = myoSauceQuantities[s.key] || 0;

                  return (
                    <div key={s.key} style={{ position: 'relative' }}>
                      <SelectTile
                        checked={quantity > 0}
                        label={s.label}
                        image={s.img}
                        rightText={quantity > 0 ? `x${quantity}` : undefined}
                        onClick={() => {
                          if (quantity > 0) {
                            const nextQuantities = { ...myoSauceQuantities };
                            delete nextQuantities[s.key];
                            setMyoSauceQuantities(nextQuantities);
                          } else {
                            setMyoSauceQuantities({ ...myoSauceQuantities, [s.key]: 1 });
                          }
                        }}
                      />
                      {quantity > 0 && (
                        <Button
                          size="small"
                          onClick={() => setSauceQuantityPopup({ sauceKey: s.key, sauceName: s.label })}
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            minWidth: 'auto',
                            padding: '2px 6px',
                            fontSize: 10,
                            backgroundColor: '#3AC28E',
                            color: 'white',
                            borderRadius: 6,
                            textTransform: 'none',
                            fontFamily: 'Montserrat, sans-serif',
                            fontWeight: 600
                          }}
                        >
                          edit
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Double Meat / Double Cheese */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontFamily: 'FontSemi', margin: 0, marginBottom: 8, color: '#333' }}>
              Extra Options
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              <SelectTile
                checked={myoDoubleMeat}
                label="Double Meat"
                rightText={`+${currency(myoHasLondonBroil ? MYO_DOUBLE_MEAT_LB : MYO_DOUBLE_MEAT)}`}
                onClick={() => setMyoDoubleMeat(!myoDoubleMeat)}
              />
              <SelectTile
                checked={myoDoubleCheese}
                label="Double Cheese"
                rightText={`+${currency(MYO_DOUBLE_CHEESE)}`}
                onClick={() => setMyoDoubleCheese(!myoDoubleCheese)}
              />
            </div>
          </div>

          {/* Toasting (single select) */}
          <div style={{ marginBottom: 10 }}>
            <h4 style={{ fontFamily: 'FontSemi', margin: 0, marginBottom: 8, color: '#333' }}>
              Toasting
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {MYO_TOAST.map((opt) => (
                <SelectTile
                  key={opt.key}
                  checked={myoToast === opt.key}
                  label={opt.label}
                  onClick={() => setMyoToast(myoToast === opt.key ? '' : opt.key)}
                />
              ))}
            </div>
          </div>

          {/* Footer actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 18 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowMakeYourOwn(false);
                resetMYO();
              }}
              startIcon={<CloseIcon />}
              style={{
                borderColor: '#bbb',
                color: '#333',
                textTransform: 'none',
                borderRadius: 12,
                padding: '10px 16px',
                fontFamily: 'Montserrat, sans-serif'
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={addMYOToCart}
              disabled={!myoSize || myoMeatCount > 3}
              style={{
                backgroundColor: '#3AC28E',
                fontFamily: 'FontSemi',
                textTransform: 'none',
                padding: '10px 20px',
                borderRadius: '12px',
                fontSize: '16px',
                opacity: (!myoSize || myoMeatCount > 3) ? 0.5 : 1
              }}
            >
              Add to Cart: {currency(myoTotal)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Sauce Quantity Popup */}
      <Dialog 
        open={!!sauceQuantityPopup} 
        onClose={() => setSauceQuantityPopup(null)} 
        maxWidth="xs" 
        fullWidth
      >
        {sauceQuantityPopup && (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <h3 style={{ fontFamily: 'FontSemi', margin: '0 0 20px 0', color: '#333' }}>
              {sauceQuantityPopup.sauceName}
            </h3>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 20,
              marginBottom: 20
            }}>
              <IconButton
                onClick={() => {
                  const currentQty = myoSauceQuantities[sauceQuantityPopup.sauceKey] || 1;
                  if (currentQty > 1) {
                    setMyoSauceQuantities({
                      ...myoSauceQuantities,
                      [sauceQuantityPopup.sauceKey]: currentQty - 1
                    });
                  }
                }}
                style={{
                  backgroundColor: '#f0f0f0',
                  width: 50,
                  height: 50
                }}
              >
                <span style={{ fontSize: 24, fontWeight: 600 }}>−</span>
              </IconButton>
              
              <div style={{
                fontSize: 32,
                fontWeight: 700,
                fontFamily: 'FontSemi',
                color: '#3AC28E',
                minWidth: 60
              }}>
                {myoSauceQuantities[sauceQuantityPopup.sauceKey] || 1}
              </div>
              
              <IconButton
                onClick={() => {
                  const currentQty = myoSauceQuantities[sauceQuantityPopup.sauceKey] || 1;
                  setMyoSauceQuantities({
                    ...myoSauceQuantities,
                    [sauceQuantityPopup.sauceKey]: currentQty + 1
                  });
                }}
                style={{
                  backgroundColor: '#3AC28E',
                  color: 'white',
                  width: 50,
                  height: 50
                }}
              >
                <span style={{ fontSize: 24, fontWeight: 600 }}>+</span>
              </IconButton>
            </div>
            
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif', 
              fontSize: 14, 
              color: '#666',
              marginBottom: 20
            }}>
              ${MYO_EXTRA_SAUCE.toFixed(2)} per extra sauce
            </div>
            
            <Button
              variant="contained"
              fullWidth
              onClick={() => setSauceQuantityPopup(null)}
              style={{
                backgroundColor: '#3AC28E',
                color: 'white',
                textTransform: 'none',
                fontFamily: 'FontSemi',
                fontSize: 16,
                padding: '10px',
                borderRadius: 10
              }}
            >
              Done
            </Button>
          </div>
        )}
      </Dialog>
      
      {/* Confirm Add Dialog */}
      <Dialog open={!!confirmAdd} onClose={() => setConfirmAdd(null)} maxWidth="xs" fullWidth>
        {confirmAdd && (
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <img
              src={confirmAdd.item.img}
              alt={confirmAdd.item.name}
              style={{
                width: 120,
                height: 100,
                objectFit: 'contain',
                borderRadius: 10,
                marginBottom: 12,
              }}
            />
            <h3 style={{ fontFamily: 'FontSemi', margin: 0, marginBottom: 8 }}>
              Add {confirmAdd.item.name} to bag?
            </h3>
            <p style={{ fontFamily: 'Montserrat, sans-serif', color: '#666', fontSize: 14 }}>
              Price: ${confirmAdd.item.price.toFixed(2)}
            </p>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
              <Button
                variant="outlined"
                onClick={() => setConfirmAdd(null)}
                style={{
                  borderColor: '#bbb',
                  color: '#333',
                  borderRadius: 10,
                  textTransform: 'none',
                  fontFamily: 'FontSemi',
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={() => {
                  const { item } = confirmAdd;
                  const newItem = {
                    id: `${item.name}-${Date.now()}`,
                    name: item.name,
                    img: item.img,
                    price: item.price,
                  };
                  setBagItems((prev) => [...prev, newItem]);
                  setConfirmAdd(null);
                }}
                style={{
                  backgroundColor: '#3AC28E',
                  borderRadius: 10,
                  textTransform: 'none',
                  fontFamily: 'FontSemi',
                }}
              >
                Add to Bag
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ClientOrders;
